import type { ComponentType } from 'react';
import type { ZodType } from 'zod';
import type { ModalId, ModalRegistry } from './modal-store';

/**
 * Props every modal component receives from the host. The payload is passed
 * explicitly (and typed by the modal id), so a component is structurally bound
 * to the id it renders — registering it under the wrong id is a type error.
 */
export interface ModalRenderProps<Id extends ModalId = ModalId> {
  payload: ModalRegistry[Id];
  /** Close this modal instance. */
  close: () => void;
  /** True while the Dialog is open (drives the exit animation). */
  open: boolean;
}

/** A modal component, typed by the payload it consumes. */
export type ModalComponent<Id extends ModalId> = ComponentType<ModalRenderProps<Id>>;

/**
 * A registry entry: the component plus an optional Zod schema that validates
 * the payload at the host boundary before the component renders.
 *
 * The schema's output must match the modal's payload type. Payloads carrying
 * non-serializable fields (e.g. a callback) can still be validated — model the
 * callback with `z.function()` / `z.custom()` (see the `confirm` schema).
 */
export interface ModalDefinition<Id extends ModalId> {
  component: ModalComponent<Id>;
  schema?: ZodType<ModalRegistry[Id]>;
}

/**
 * The full registry: every modal id mapped to its definition. Distributed over
 * `ModalId` so each entry's component/schema are checked against THAT id's
 * payload — `'create-note'` cannot be mapped to a confirm component.
 */
export type ModalRegistryMap = {
  [Id in ModalId]: ModalDefinition<Id>;
};

/**
 * Identity helper that pins a single registry entry to its id, so the component
 * and schema are inferred and checked together at the registration site:
 *
 * ```ts
 * defineModal('create-note', { component: CreateNoteModal, schema: createNotePayloadSchema })
 * ```
 */
export function defineModal<Id extends ModalId>(
  _id: Id,
  definition: ModalDefinition<Id>,
): ModalDefinition<Id> {
  return definition;
}
